<template>
  <custom-dialog
    color="success"
    icon="mdi-file-outline"
    ref="dialog"
    store-modal
    :title="generateMessage('requests.actions.workorder_report')"
  >
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-select
        class="mx-2"
        v-model="body.state"
        item-text="name"
        item-value="id"
        :items="states"
        :label="generateMessage('requests.actions.status') + ' *'"
        @input="$refs.form.resetValidation()"
        color="success"
        outlined
        dense
      />
      <custom-technician-search-field
        v-model="tech"
        :disabled="isWaiting"
        :entityId="entity_nav_full_id"
        :property-id="propertyId"
        :request="request"
        ref="tech"
      />
      <v-autocomplete
        :items="reasons"
        v-model="body.diagnosis"
        :rules="[(v) => !!v || 'Ce champ est obligatoire']"
        :disabled="!isClosing"
        :placeholder="generateMessage('requests.actions.diagnosis') + ' *'"
        clearable
        outlined
        dense
        item-text="diagnostic_qualif_name"
        item-value="diagnostic_qualif_id"
        hide-selected
        color="success"
        class="mx-2"
      />
      <v-autocomplete
        :items="treatments"
        v-model="body.treatment_id"
        :rules="[(v) => !!v || 'Ce champ est obligatoire']"
        :disabled="!isClosing"
        :placeholder="generateMessage('requests.actions.treatment') + ' *'"
        clearable
        outlined
        dense
        item-text="name"
        item-value="id"
        hide-selected
        color="success"
        class="mx-2"
      />
      <div class="d-flex">
        <v-select
          class="mx-2"
          style="width: 50%"
          v-model="body.service_id"
          :disabled="!isClosing"
          item-text="id"
          item-value="id"
          :items="services"
          :label="generateMessage('requests.actions.prestation') + ' *'"
          :rules="[(v) => !!v || 'Ce champ est obligatoire']"
          color="success"
          clearable
          outlined
          dense
        />
        <v-text-field
          :label="generateMessage('requests.actions.team_number')"
          class="mx-2"
          :disabled="!isClosing"
          v-model="body.equipment_number"
          outlined
          :rules="[(v) => v.length <= 15 || 'Maximum 15 caractères.']"
          style="width: 50%"
          color="success"
        />
      </div>
      <div class="d-flex">
        <v-text-field
          :label="generateMessage('requests.actions.begin_datetime') + ' *'"
          class="mx-2"
          color="success"
          :disabled="!isClosing"
          outlined
          :rules="[
            (v) => !!v || 'Ce champ est obligatoire',
            (v) =>
              v < formatDateToInput(new Date()) ||
              'La date ne doit pas être dans le futur',
          ]"
          v-model="body.intervention_start_datetime"
          @input="updateDuration"
          style="width: 50%"
          type="datetime-local"
        />
        <v-text-field
          :label="generateMessage('requests.actions.end_datetime') + ' *'"
          class="mx-2"
          style="width: 50%"
          :disabled="!isClosing"
          outlined
          v-model="body.intervention_end_datetime"
          :rules="[
            (v) => !!v || 'Ce champ est obligatoire',
            (v) =>
              this.body.intervention_start_datetime < v ||
              'Les dates doivent correspondre',
            (v) =>
              v < formatDateToInput(new Date()) ||
              'La date ne doit pas être dans le futur',
          ]"
          @input="updateDuration"
          color="success"
          type="datetime-local"
        />
      </div>
      <div class="d-flex">
        <v-text-field
          :label="generateMessage('requests.actions.duration')"
          class="mx-2"
          style="width: 50%"
          :disabled="!isClosing"
          :rules="[(v) => !!v || '']"
          outlined
          readonly
          :value="body.intervention_duration | displayDuration"
          color="success"
        />

        <v-text-field
          :label="generateMessage('requests.actions.shifting')"
          class="mx-2"
          style="width: 50%"
          :disabled="!isClosing"
          v-model="body.intervention_shifting"
          outlined
          color="success"
          type="number"
        />
      </div>
      <div class="d-flex">
        <v-radio-group
          :label="generateMessage('requests.actions.remote') + ' *'"
          :disabled="!isClosing"
          v-model="body.remotly"
          :rules="[(v) => !!v || 'Ce champ est obligatoire']"
          style="width: 50%"
        >
          <v-radio
            :label="generateMessage('requests.actions.yes')"
            color="success"
            value="O"
          />
          <v-radio
            :label="generateMessage('requests.actions.no')"
            color="success"
            value="N"
          />
        </v-radio-group>
        <v-radio-group
          style="width: 50%"
          :label="generateMessage('requests.actions.ended') + ' *'"
          :disabled="!isClosing"
          :rules="[(v) => !!v || 'Ce champ est obligatoire']"
          v-model="body.repaired"
        >
          <v-radio
            :label="generateMessage('requests.actions.yes')"
            color="success"
            value="1"
          />
          <v-radio
            :label="generateMessage('requests.actions.yes_continued')"
            color="success"
            value="2"
          />
        </v-radio-group>
      </div>
      <v-select
        class="mx-2"
        style="width: 50%"
        :items="reasonsToBeContinued"
        item-text="short_name"
        item-value="id"
        v-if="body.repaired === '2'"
        :disabled="!isClosing"
        clearable
        :rules="[
          (v) => body.repaired !== '2' || !!v || 'Ce champ est obligatoire',
        ]"
        :label="generateMessage('requests.actions.reason') + ' *'"
        v-model="body.reason_to_be_continued"
        @input="setComment"
        color="success"
        outlined
        dense
      />

      <custom-text-area
        class="mx-2"
        color="success"
        :maxCharacter="200"
        :disabled="!isClosing"
        errorTooLongMsg="Maximun 200 caractères."
        v-model="body.report_comment"
        :label="generateMessage('requests.actions.comment')"
      />
    </v-form>
    <template v-slot:actions>
      <div class="d-flex justify-end">
        <v-btn
          small
          color="success"
          class="mx-2"
          :loading="isLoading"
          :disabled="!tech || !body.state"
          @click="onValidate"
        >
          {{ generateMessage('requests.actions.validate') }}
        </v-btn>
        <v-btn small class="mx-2" @click="cancel">
          {{ generateMessage('requests.actions.cancel') }}
        </v-btn>
      </div>
    </template>
  </custom-dialog>
</template>

<script>
import { generateMessage } from '@/utils/i18n';
import { mapGetters } from 'vuex';
import CustomDialog from '@/components/dialogs/CustomDialog';
import CustomTechnicianSearchField from '@/pages/eDispatch/requestActions/eDispatchTechnicianSearchField';
import CustomTextArea from '@/components/CustomTextArea';
import {
  directReport,
  findTechByGroupId,
} from '@/services/api/internal/requestActions/directReport.api';
import { setDispActive } from '@/services/api/internal/requestActions/selfAssign.api';
import { catchCRUDError } from '@/business/apiTreatment/response/errorTreatment';
import { formatDateToInput } from '@/business/utils/dates';

export default {
  name: 'WorkorderReportDialog',
  components: {
    CustomDialog,
    CustomTechnicianSearchField,
    CustomTextArea,
  },
  props: {
    entity_nav_full_id: {
      type: String,
      required: true,
    },
    isAssigned: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPaused: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPemHis: {
      type: Boolean,
      required: false,
      default: false,
    },
    propertyId: {
      type: Number,
      required: true,
    },
    qualification: {
      type: Number,
      required: false,
    },
    requestId: {
      type: Number,
      required: true,
    },
    request: {
      type: Object,
      required: false,
    },
    status: {
      type: String,
      required: true,
    },
    symptom: {
      type: Number,
      required: false,
    },
    technician_internal_id: {
      type: Number,
      required: false,
    },
  },
  computed: {
    ...mapGetters({
      initialTech: 'technicians/current',
      states: 'requests/states/items',
      reasons: 'requests/history/reason/items',
      treatments: 'requests/treatments/items',
      services: 'requests/services/items',
      reasonsToBeContinued: 'requests/reasonsToBeContinued/items',
    }),
    isClosing() {
      return this.body.state === '5';
    },
    isWaiting() {
      return this.body.state === '3';
    },
  },
  filters: {
    displayDuration(hour) {
      if (hour && hour > 0) {
        const h = Math.trunc(hour);
        const min = Math.trunc(hour * 60) - 60 * h;
        return `${h !== 0 ? `${h}h` : ''}${
          h !== 0 && min < 10 ? `0${min}` : min
        }min`;
      }
      return '';
    },
  },
  methods: {
    findTechByGroupId,
    directReport,
    setDispActive,
    generateMessage,
    formatDateToInput,
    updateDuration() {
      if (
        this.body.intervention_start_datetime &&
        this.body.intervention_end_datetime
      ) {
        this.body.intervention_duration =
          (new Date(this.body.intervention_end_datetime) -
            new Date(this.body.intervention_start_datetime)) /
          (60 * 60 * 1000);
      }
    },
    parseFloat(text) {
      return text && parseFloat(text.slice(0, text.indexOf('.') + 3));
    },
    cancel() {
      this.$refs.dialog.handleCloseClick();
    },
    onValidate() {
      if (
        !this.isClosing
          ? this.$refs.tech.validate()
          : this.$refs.form.validate()
      ) {
        this.isLoading = true;
        const body = {
          ...this.body,
          technician_internal_id: this.tech.id,
          technician_phone: this.tech.phone,
          intervention_shifting: this.parseFloat(
            this.body.intervention_shifting
          ),
        };
        this.setDispActive({
          requestId: this.requestId,
          groupId: this.groupId,
          isPaused: this.isPaused,
          isAssigned: this.isAssigned,
        })
          .then(async () => {
            await this.directReport(this.requestId, body)
              .then(() => {
                this.cancel();
                this.$store.dispatch('requests/refreshItems');
                this.$store.dispatch('requests/removeCurrent', this.requestId);
                this.$store.dispatch('boilerplate/setModalClosed');
              })
              .catch((err) => {
                this.$store.dispatch(
                  'snackbar/setSnackbarOpen',
                  catchCRUDError('EDIT', err)
                );
              });
          })
          .catch((err) => {
            this.$store.dispatch(
              'snackbar/setSnackbarOpen',
              catchCRUDError('FREE', err)
            );
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    initBody() {
      this.body = {
        state: this.status,
        diagnosis: null,
        treatment_id: null,
        service_id: null,
        equipment_number: '',
        intervention_start_datetime: null,
        intervention_end_datetime: null,
        intervention_duration: null,
        intervention_shifting: null,
        remotly: null,
        repaired: null,
        reason_to_be_continued: null,
        report_comment: '',
      };
      this.tech = {};

      if (
        this.isPemHis &&
        this.symptom === 1 &&
        (this.qualification === 9 ||
          this.qualification === 10 ||
          this.qualification === 178)
      ) {
        this.body = {
          ...this.body,
          state: '5',
          diagnosis: '1-6',
          service_id: 'P22000',
          remotly: 'O',
        };
        if (this.qualification === 9) {
          this.body = {
            ...this.body,
            treatment_id: 1,
          };
        } else if (this.qualification === 10) {
          this.body = {
            ...this.body,
            treatment_id: 2,
          };
        }
      }
    },
    setComment() {
      if (this.isPemHis && this.symptom === 1) {
        let autofilledComment = '';
        if (this.qualification === 9)
          autofilledComment = this.generateMessage(
            'requests.actions.autofilled_comment_ignition'
          );
        else if (this.qualification === 10)
          autofilledComment = this.generateMessage(
            'requests.actions.autofilled_comment_shutdown'
          );
        else if (this.qualification === 178)
          autofilledComment = this.generateMessage(
            'requests.actions.autofilled_comment'
          );

        if (
          this.qualification === 9 ||
          this.qualification === 10 ||
          this.qualification === 178
        ) {
          if (
            this.body &&
            this.body.reason_to_be_continued === '4' &&
            this.body.report_comment === ''
          ) {
            this.body = {
              ...this.body,
              report_comment: autofilledComment,
            };
          } else if (
            this.body &&
            this.body.reason_to_be_continued !== '4' &&
            this.body.report_comment === autofilledComment
          ) {
            this.body = {
              ...this.body,
              report_comment: '',
            };
          }
        }
      }
    },
  },
  watch: {
    technician_internal_id: {
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch('technicians/initCurrent', newVal);
        } else {
          const groupId = this.$store.getters['global/groupId'];
          this.findTechByGroupId(groupId).then((tech) => {
            this.tech = tech;
            this.body.state = '3';
          });
        }
      },
      immediate: true,
    },
    initialTech(newVal) {
      this.tech = newVal;
    },
    requestId: {
      handler() {
        this.initBody();
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
        if (this.$refs.tech) {
          this.$refs.tech.resetValidation();
        }
      },
      immediate: true,
    },
    status: {
      handler() {
        this.initBody();
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
        if (this.$refs.tech) {
          this.$refs.tech.resetValidation();
        }
      },
    },
  },
  data() {
    return {
      tech: {},
      body: {},
      valid: false,
      techEmpty: false,
      isLoading: false,
    };
  },
};
</script>
